import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import CircularProgress from '@mui/material/CircularProgress';
import Autocomplete from '@mui/material/Autocomplete';
import { styled } from '@mui/material/styles';
import { ArrowLeft, Boxes, User, MapPin } from 'lucide-react';
import api from '../services/api';
import { toast } from 'react-toastify';

const StyledCard = styled(Card)({
  boxShadow:
    '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden'
});

const statusColor = (status) => {
  switch (status) {
    case 'DISPONIBILE':
      return 'success';
    case 'ASSEGNATO':
      return 'primary';
    case 'IN_MANUTENZIONE':
      return 'warning';
    case 'DISMESSO':
      return 'default';
    case 'PERSO':
      return 'error';
    default:
      return 'default';
  }
};

export default function AssetDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [offices, setOffices] = useState([]);
  const [audit, setAudit] = useState([]);

  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignForm, setAssignForm] = useState({
    user_id: '',
    office_id: '',
    assignment_type: 'SEDE',
    start_date: '',
    end_date: '',
    reason: '',
    notes: ''
  });

  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [returnForm, setReturnForm] = useState({
    end_date: '',
    reason: '',
    notes: ''
  });

  const [maintenanceDialogOpen, setMaintenanceDialogOpen] = useState(false);
  const [maintenanceForm, setMaintenanceForm] = useState({
    maintenance_type: '',
    open_date: '',
    provider: '',
    ticket_number: '',
    cost: '',
    notes: ''
  });
  const [closeMaintenanceDialogOpen, setCloseMaintenanceDialogOpen] = useState(false);
  const [maintenanceToClose, setMaintenanceToClose] = useState(null);
  const [closeMaintenanceForm, setCloseMaintenanceForm] = useState({
    close_date: '',
    status: 'CHIUSA',
    final_status: '',
    notes: ''
  });

  const loadReferenceData = async () => {
    try {
      const [userRes, officeRes] = await Promise.all([
        api.get('/users', { params: { page: 1, limit: 1000 } }),
        api.get('/office')
      ]);
      setUsers(userRes.data?.users || []);
      setOffices(officeRes.data || []);
    } catch (error) {
      console.error('Error loading users/offices', error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [assetRes, auditRes] = await Promise.all([
        api.get(`/assets/${id}`),
        api.get(`/assets/${id}/audit`)
      ]);
      setData(assetRes.data || null);
      setAudit(auditRes.data || []);
    } catch (error) {
      console.error('Error loading asset detail', error);
      toast.error('Errore nel caricamento del dettaglio asset');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReferenceData();
    fetchData();
  }, [id]);

  const asset = data?.asset;
  const assignments = data?.assignments || [];
  const movements = data?.movements || [];
  const maintenances = data?.maintenances || [];
  const disposals = data?.disposals || [];

  const activeAssignment = assignments.find((a) => a.status === 'ATTIVA');

  const handleOpenAssignDialog = () => {
    setAssignForm({
      user_id: '',
      office_id: '',
      assignment_type: 'SEDE',
      start_date: '',
      end_date: '',
      reason: '',
      notes: ''
    });
    setAssignDialogOpen(true);
  };

  const handleAssignChange = (field) => (event) => {
    setAssignForm((prev) => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleCreateAssignment = async () => {
    if (!assignForm.user_id && !assignForm.office_id) {
      toast.error('Seleziona un utente o una sede');
      return;
    }
    try {
      await api.post(`/assets/${id}/assignments`, {
        user_id: assignForm.user_id || null,
        office_id: assignForm.office_id || null,
        assignment_type: assignForm.assignment_type,
        start_date: assignForm.start_date || null,
        end_date: assignForm.end_date || null,
        reason: assignForm.reason || null,
        notes: assignForm.notes || null
      });
      toast.success('Assegnazione creata');
      setAssignDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error creating assignment', error);
      toast.error('Errore nella creazione dell\'assegnazione');
    }
  };

  const handleOpenReturnDialog = () => {
    if (!activeAssignment) {
      toast.error('Nessuna assegnazione attiva da chiudere');
      return;
    }
    setReturnForm({
      end_date: '',
      reason: '',
      notes: ''
    });
    setReturnDialogOpen(true);
  };

  const handleReturnChange = (field) => (event) => {
    setReturnForm((prev) => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleCloseAssignment = async () => {
    if (!activeAssignment) {
      setReturnDialogOpen(false);
      return;
    }
    try {
      await api.post(`/assets/assignments/${activeAssignment.id}/close`, {
        end_date: returnForm.end_date || null,
        reason: returnForm.reason || null,
        notes: returnForm.notes || null
      });
      toast.success('Assegnazione chiusa');
      setReturnDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error closing assignment', error);
      toast.error('Errore nella chiusura dell\'assegnazione');
    }
  };

  const handleMarkLost = async () => {
    if (!window.confirm('Segnare questo asset come perso?')) {
      return;
    }
    try {
      await api.post(`/assets/${id}/lost`, {});
      toast.success('Asset segnato come perso');
      fetchData();
    } catch (error) {
      console.error('Error marking asset lost', error);
      toast.error('Errore nella marcatura dell\'asset come perso');
    }
  };

  const handleRetire = async () => {
    if (!window.confirm('Dismetter questo asset?')) {
      return;
    }
    try {
      await api.post(`/assets/${id}/retire`, {});
      toast.success('Asset dismesso');
      fetchData();
    } catch (error) {
      console.error('Error retiring asset', error);
      toast.error('Errore nella dismissione dell\'asset');
    }
  };

  const handleOpenMaintenanceDialog = () => {
    setMaintenanceForm({
      maintenance_type: '',
      open_date: '',
      provider: '',
      ticket_number: '',
      cost: '',
      notes: ''
    });
    setMaintenanceDialogOpen(true);
  };

  const handleMaintenanceChange = (field) => (event) => {
    setMaintenanceForm((prev) => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleCreateMaintenance = async () => {
    if (!maintenanceForm.maintenance_type) {
      toast.error('Tipo manutenzione obbligatorio');
      return;
    }
    try {
      await api.post(`/assets/${id}/maintenances`, {
        maintenance_type: maintenanceForm.maintenance_type,
        open_date: maintenanceForm.open_date || null,
        provider: maintenanceForm.provider || null,
        ticket_number: maintenanceForm.ticket_number || null,
        cost: maintenanceForm.cost ? Number(maintenanceForm.cost) : null,
        notes: maintenanceForm.notes || null
      });
      toast.success('Manutenzione creata');
      setMaintenanceDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error creating maintenance', error);
      toast.error('Errore nella creazione della manutenzione');
    }
  };

  const handleOpenCloseMaintenanceDialog = (row) => {
    setMaintenanceToClose(row);
    setCloseMaintenanceForm({
      close_date: '',
      status: 'CHIUSA',
      final_status: '',
      notes: ''
    });
    setCloseMaintenanceDialogOpen(true);
  };

  const handleCloseMaintenanceChange = (field) => (event) => {
    setCloseMaintenanceForm((prev) => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleCloseMaintenance = async () => {
    if (!maintenanceToClose) {
      setCloseMaintenanceDialogOpen(false);
      return;
    }
    try {
      await api.post(`/assets/maintenances/${maintenanceToClose.id}/close`, {
        close_date: closeMaintenanceForm.close_date || null,
        status: closeMaintenanceForm.status || 'CHIUSA',
        final_status: closeMaintenanceForm.final_status || null,
        notes: closeMaintenanceForm.notes || null
      });
      toast.success('Manutenzione chiusa');
      setCloseMaintenanceDialogOpen(false);
      setMaintenanceToClose(null);
      fetchData();
    } catch (error) {
      console.error('Error closing maintenance', error);
      toast.error('Errore nella chiusura della manutenzione');
    }
  };

  if (loading && !asset) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!asset) {
    return (
      <Box sx={{ p: 3 }}>
        <Button
          variant="text"
          startIcon={<ArrowLeft size={16} />}
          onClick={() => navigate('/assets')}
          sx={{ mb: 2 }}
        >
          Torna alla lista asset
        </Button>
        <Typography variant="h6">Asset non trovato</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Button
            variant="text"
            startIcon={<ArrowLeft size={16} />}
            onClick={() => navigate('/assets')}
          >
            Indietro
          </Button>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Boxes size={20} color="#4f46e5" />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {asset.description}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Codice {asset.asset_code}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => navigate(`/assets/${id}/edit`)}
          >
            Modifica
          </Button>
          <Chip
            label={asset.current_status}
            color={statusColor(asset.current_status)}
            size="small"
          />
        </Box>
      </Box>

      <StyledCard>
        <Box sx={{ p: 3, display: 'flex', flexDirection: 'row', gap: 4, flexWrap: 'wrap' }}>
          <Box sx={{ minWidth: 260, flex: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Dettagli asset
            </Typography>
            <Box sx={{ mt: 1.5, display: 'grid', gridTemplateColumns: '140px 1fr', rowGap: 0.75 }}>
              <Typography variant="body2" color="text.secondary">
                Codice
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                {asset.asset_code}
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Categoria
              </Typography>
              <Typography variant="body2">
                {asset.category_name || asset.category_code || '-'}
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Modello
              </Typography>
              <Typography variant="body2">
                {asset.model || '-'}
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Serial
              </Typography>
              <Typography variant="body2">
                {asset.serial_number || '-'}
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Fornitore
              </Typography>
              <Typography variant="body2">
                {asset.supplier_name || asset.supplier || '-'}
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Sede
              </Typography>
              <Typography variant="body2">
                {asset.office_name || '-'}
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Proprietà
              </Typography>
              <Typography variant="body2">
                {asset.ownership_type}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ minWidth: 260, flex: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Assegnazione corrente
            </Typography>
            <Box sx={{ mt: 1.5 }}>
              {activeAssignment ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        bgcolor: '#eef2ff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <User size={18} color="#4f46e5" />
                    </Box>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {activeAssignment.user_name || `ID ${activeAssignment.user_id}`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {activeAssignment.user_email || ''}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <MapPin size={16} color="#0f766e" />
                    <Typography variant="body2">
                      {activeAssignment.office_name || asset.office_name || '-'}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Tipo utilizzo: {activeAssignment.assignment_type}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Inizio:{' '}
                    {activeAssignment.start_date
                      ? new Date(activeAssignment.start_date).toLocaleString('it-IT')
                      : '-'}
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Nessuna assegnazione attiva
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ minWidth: 260 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Azioni
            </Typography>
            <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button
                variant="contained"
                size="small"
                disabled={asset.current_status !== 'DISPONIBILE'}
                onClick={handleOpenAssignDialog}
              >
                Assegna
              </Button>
              <Button
                variant="outlined"
                size="small"
                disabled={!activeAssignment}
                onClick={handleOpenReturnDialog}
              >
                Restituisci
              </Button>
              <Button
                variant="outlined"
                color="error"
                size="small"
                disabled={asset.current_status === 'PERSO'}
                onClick={handleMarkLost}
              >
                Segna come perso
              </Button>
              <Button
                variant="outlined"
                color="warning"
                size="small"
                disabled={asset.current_status === 'DISMESSO'}
                onClick={handleRetire}
              >
                Dismetti
              </Button>
            </Box>
          </Box>
        </Box>
      </StyledCard>

      <StyledCard>
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Assegnazioni
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Stato</TableCell>
                  <TableCell>Assegnatario</TableCell>
                  <TableCell>Sede</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Inizio</TableCell>
                  <TableCell>Fine</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assignments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography variant="body2" color="text.secondary">
                        Nessuna assegnazione registrata.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  assignments.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>
                        <Chip label={row.status} size="small" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {row.user_name || (row.user_id ? `ID ${row.user_id}` : '-')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {row.office_name || '-'}
                      </TableCell>
                      <TableCell>{row.assignment_type}</TableCell>
                      <TableCell>
                        {row.start_date
                          ? new Date(row.start_date).toLocaleString('it-IT')
                          : '-'}
                      </TableCell>
                      <TableCell>
                        {row.end_date
                          ? new Date(row.end_date).toLocaleString('it-IT')
                          : '-'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </StyledCard>

      <StyledCard>
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Movimenti
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Data</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Da</TableCell>
                  <TableCell>A</TableCell>
                  <TableCell>Stato</TableCell>
                  <TableCell>Motivo</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {movements.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography variant="body2" color="text.secondary">
                        Nessun movimento registrato.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  movements.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>
                        {row.movement_date
                          ? new Date(row.movement_date).toLocaleString('it-IT')
                          : '-'}
                      </TableCell>
                      <TableCell>{row.movement_type}</TableCell>
                      <TableCell>{row.from_status || '-'}</TableCell>
                      <TableCell>{row.to_status || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={row.to_status || '-'}
                          size="small"
                          color={statusColor(row.to_status)}
                        />
                      </TableCell>
                      <TableCell>{row.reason || '-'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </StyledCard>

      <StyledCard>
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Manutenzioni
          </Typography>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              size="small"
              onClick={handleOpenMaintenanceDialog}
            >
              Nuova manutenzione
            </Button>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Stato</TableCell>
                  <TableCell>Fornitore</TableCell>
                  <TableCell>Apertura</TableCell>
                  <TableCell>Chiusura</TableCell>
                  <TableCell>Costo</TableCell>
                  <TableCell align="right">Azioni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {maintenances.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography variant="body2" color="text.secondary">
                        Nessuna manutenzione registrata.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  maintenances.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{row.maintenance_type}</TableCell>
                      <TableCell>
                        <Chip label={row.status} size="small" />
                      </TableCell>
                      <TableCell>{row.provider || '-'}</TableCell>
                      <TableCell>
                        {row.open_date
                          ? new Date(row.open_date).toLocaleString('it-IT')
                          : '-'}
                      </TableCell>
                      <TableCell>
                        {row.close_date
                          ? new Date(row.close_date).toLocaleString('it-IT')
                          : '-'}
                      </TableCell>
                      <TableCell>
                        {row.cost != null ? `${row.cost.toFixed(2)} €` : '-'}
                      </TableCell>
                      <TableCell align="right">
                        {row.status !== 'CHIUSA' && row.status !== 'ANNULLATA' && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleOpenCloseMaintenanceDialog(row)}
                          >
                            Chiudi
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </StyledCard>

      <StyledCard>
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Smarrimenti e dismissioni
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Data</TableCell>
                  <TableCell>Motivo</TableCell>
                  <TableCell>Documento</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {disposals.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography variant="body2" color="text.secondary">
                        Nessun evento registrato.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  disposals.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{row.disposal_type}</TableCell>
                      <TableCell>
                        {row.disposal_date
                          ? new Date(row.disposal_date).toLocaleString('it-IT')
                          : '-'}
                      </TableCell>
                      <TableCell>{row.reason || '-'}</TableCell>
                      <TableCell>{row.document_ref || '-'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </StyledCard>

      <StyledCard>
          <Box sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Audit asset
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Data</TableCell>
                  <TableCell>Azione</TableCell>
                  <TableCell>Utente</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {audit.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3}>
                      <Typography variant="body2" color="text.secondary">
                        Nessuna modifica registrata.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  audit.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>
                        {row.changed_at
                          ? new Date(row.changed_at).toLocaleString('it-IT')
                          : '-'}
                      </TableCell>
                      <TableCell>{row.action}</TableCell>
                      <TableCell>
                        {row.user_name
                          ? `${row.user_name} (${row.user_email || ''})`
                          : row.changed_by_user_id}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </StyledCard>

      <Dialog
        open={assignDialogOpen}
        onClose={() => setAssignDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Nuova assegnazione</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Autocomplete
              size="small"
              options={users}
              getOptionLabel={(u) => `${u.name} (${u.email})`}
              value={users.find((u) => u.id === assignForm.user_id) || null}
              onChange={(event, newValue) =>
                setAssignForm((prev) => ({
                  ...prev,
                  user_id: newValue ? newValue.id : '',
                  office_id: newValue ? '' : prev.office_id
                }))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Utente"
                  placeholder="Cerca utente per nome o email"
                  fullWidth
                />
              )}
            />
            <FormControl fullWidth size="small">
              <InputLabel>Sede</InputLabel>
              <Select
                label="Sede"
                value={assignForm.office_id}
                onChange={(event) =>
                  setAssignForm((prev) => ({
                    ...prev,
                    office_id: event.target.value,
                    user_id: event.target.value ? prev.user_id : prev.user_id
                  }))
                }
              >
                <MenuItem value="">
                  <em>Non specificata</em>
                </MenuItem>
                {offices.map((office) => (
                  <MenuItem key={office.id} value={office.id}>
                    {office.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Tipo utilizzo</InputLabel>
              <Select
                label="Tipo utilizzo"
                value={assignForm.assignment_type}
                onChange={handleAssignChange('assignment_type')}
              >
                <MenuItem value="SEDE">Uso in sede</MenuItem>
                <MenuItem value="SMART_WORKING">Smart working</MenuItem>
                <MenuItem value="TRASFERTA">Trasferta</MenuItem>
                <MenuItem value="MISTO">Misto</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Data inizio"
              type="datetime-local"
              size="small"
              value={assignForm.start_date}
              onChange={handleAssignChange('start_date')}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Data fine prevista"
              type="datetime-local"
              size="small"
              value={assignForm.end_date}
              onChange={handleAssignChange('end_date')}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Motivo"
              size="small"
              value={assignForm.reason}
              onChange={handleAssignChange('reason')}
              fullWidth
            />
            <TextField
              label="Note"
              size="small"
              multiline
              minRows={2}
              value={assignForm.notes}
              onChange={handleAssignChange('notes')}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)}>Annulla</Button>
          <Button variant="contained" onClick={handleCreateAssignment}>
            Salva assegnazione
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={returnDialogOpen}
        onClose={() => setReturnDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Restituzione asset</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Data restituzione"
              type="datetime-local"
              size="small"
              value={returnForm.end_date}
              onChange={handleReturnChange('end_date')}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Motivo"
              size="small"
              value={returnForm.reason}
              onChange={handleReturnChange('reason')}
              fullWidth
            />
            <TextField
              label="Note"
              size="small"
              multiline
              minRows={2}
              value={returnForm.notes}
              onChange={handleReturnChange('notes')}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReturnDialogOpen(false)}>Annulla</Button>
          <Button variant="contained" onClick={handleCloseAssignment}>
            Conferma restituzione
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={maintenanceDialogOpen}
        onClose={() => setMaintenanceDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Nuova manutenzione</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Tipo manutenzione"
              size="small"
              value={maintenanceForm.maintenance_type}
              onChange={handleMaintenanceChange('maintenance_type')}
              fullWidth
            />
            <TextField
              label="Data apertura"
              type="datetime-local"
              size="small"
              value={maintenanceForm.open_date}
              onChange={handleMaintenanceChange('open_date')}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Fornitore"
              size="small"
              value={maintenanceForm.provider}
              onChange={handleMaintenanceChange('provider')}
              fullWidth
            />
            <TextField
              label="Ticket"
              size="small"
              value={maintenanceForm.ticket_number}
              onChange={handleMaintenanceChange('ticket_number')}
              fullWidth
            />
            <TextField
              label="Costo"
              type="number"
              size="small"
              value={maintenanceForm.cost}
              onChange={handleMaintenanceChange('cost')}
              fullWidth
            />
            <TextField
              label="Note"
              size="small"
              multiline
              minRows={2}
              value={maintenanceForm.notes}
              onChange={handleMaintenanceChange('notes')}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMaintenanceDialogOpen(false)}>Annulla</Button>
          <Button variant="contained" onClick={handleCreateMaintenance}>
            Salva manutenzione
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={closeMaintenanceDialogOpen}
        onClose={() => setCloseMaintenanceDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Chiudi manutenzione</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Data chiusura"
              type="datetime-local"
              size="small"
              value={closeMaintenanceForm.close_date}
              onChange={handleCloseMaintenanceChange('close_date')}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <FormControl fullWidth size="small">
              <InputLabel>Stato manutenzione</InputLabel>
              <Select
                label="Stato manutenzione"
                value={closeMaintenanceForm.status}
                onChange={handleCloseMaintenanceChange('status')}
              >
                <MenuItem value="CHIUSA">Chiusa</MenuItem>
                <MenuItem value="ANNULLATA">Annullata</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Stato finale asset</InputLabel>
              <Select
                label="Stato finale asset"
                value={closeMaintenanceForm.final_status}
                onChange={handleCloseMaintenanceChange('final_status')}
              >
                <MenuItem value="">
                  <em>Non modificare</em>
                </MenuItem>
                <MenuItem value="DISPONIBILE">Disponibile</MenuItem>
                <MenuItem value="DISMESSO">Dismesso</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Note"
              size="small"
              multiline
              minRows={2}
              value={closeMaintenanceForm.notes}
              onChange={handleCloseMaintenanceChange('notes')}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloseMaintenanceDialogOpen(false)}>Annulla</Button>
          <Button variant="contained" onClick={handleCloseMaintenance}>
            Conferma chiusura
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
